import {
  TrackingPropsInterface,
  TrackingProps,
} from '../organisms/MerchandisingHeroBanner/MerchandisingHeroBanner.types';
import { IMetaData, ITargetContent } from '../types/dcaTypes';
import { ITargetContent as ITargetContentCarouselCard } from '../organisms/MerchandisingHeroCarouselCard/MerchandisingHeroCarouselCard.types';
import { MangoAssests } from '../organisms/RiottMediaCard/RiottMediaCard.types';
import { canUseDOM } from '@marriott/shared/mi-helper-utils';
import { getEVAR48, getSPID } from '@marriott/mi-headless-utils';

export const getTrackingPropertiesDynamic = (
  trackingProperties: TrackingProps,
  metaData: IMetaData,
  targetData: Partial<ITargetContent> | Partial<ITargetContentCarouselCard>,
  isAuth: boolean,
  isTargetContentError: boolean,
  mboxParameter = ''
): TrackingPropsInterface => {
  const hasResolvedMetaData = Object.values((metaData ?? {}) as Record<string, unknown>).some(
    value => value !== undefined && value !== null && value !== ''
  );

  /**
   * @returns it returns the value of atCCeVar48
   * @description this fucntion returns value of atCCeVar48, conditionally,
   *
   */
  const getAtCCevar48 = () => {
    const isCobrand =
      isTargetContentError || !mboxParameter
        ? trackingProperties?.merchandisingCategory === 'cobrandNonPointsMath'
        : metaData?.type === 'CoBrand';

    if (canUseDOM && isCobrand) {
      // If type in MBOP response is cobrand then construct evar48
      if (isTargetContentError || !mboxParameter) return trackingProperties?.atCCeVar48;
      else return getEVAR48(targetData);
    }
    // if type in MBOP response is non cobrand then pass empty in evar48
    return '';
  };

  const resolvedTrackingContentPosition = hasResolvedMetaData
    ? mboxParameter?.replace(/-mbox$/, '')
    : trackingProperties?.trackingContentPosition;
  const resolvedTrackingDescription = hasResolvedMetaData ? 'dca' : trackingProperties?.trackingDescription;
  const resolvedTrackingOfferType = hasResolvedMetaData ? 'dca' : trackingProperties?.trackingOfferType;
  const resolvedTrackingTag = hasResolvedMetaData
    ? String(metaData?.merTag)
    : trackingProperties?.trackingTag1 ?? trackingProperties?.trackingTag;

  return {
    trackingContentPosition: resolvedTrackingContentPosition,
    trackingDescription: resolvedTrackingDescription,
    trackingOfferType: resolvedTrackingOfferType,
    trackingTag: resolvedTrackingTag,
    additionalTrackingVariables: hasResolvedMetaData
      ? metaData?.optionalFields
        ? 'true'
        : 'false'
      : trackingProperties?.atCCeVar48,
    atCCeVar48: getAtCCevar48(),
    clickTrack: trackingProperties?.clickTrack ?? true,
    impressionTrack: trackingProperties?.impressionTrack ?? true,
    impressionCount: trackingProperties?.impressionCount ?? true,
    isAuth: isAuth,
    merchandisingCategory: hasResolvedMetaData ? metaData?.type : trackingProperties?.merchandisingCategory,
    isDCAEnabled: true,
    // only pick when we have UXL response
    spid: hasResolvedMetaData ? getSPID({ metaData: metaData }) : '',
    rpcCode: hasResolvedMetaData ? metaData?.rpcCode || '' : '',
    cell: hasResolvedMetaData ? metaData?.cell || '' : '',
    offerCode: hasResolvedMetaData ? metaData?.offerCode || '' : '',
    type: hasResolvedMetaData ? metaData?.type || '' : '',
    clickTrackValSeparator: ';',
  };
};

/**
 *
 * @returns it returns updated class based on condition
 * @param originalClass it refers to the original class
 * @param updatedClass it refers to updated class
 * @param condition it refers to condition
 * @description this fucntion returns original class by default or if condition is false else updated class.
 *
 */
export const updateClass = (originalClass: string, updatedClass: string, condition: boolean) => {
  switch (condition) {
    case true:
      return updatedClass;

    case false:
      return originalClass;

    default:
      return originalClass;
  }
};

/**
 *
 * @returns it returns the AEM tracking properties
 * @param trackingProperties it refers to the AEM tracking properties
 * @param isAuth it refers to auth
 * @description this fucntion returns value of AEM tracking properties without depending on UXL response.
 *
 */
export const getAEMTrackingProperties = (
  trackingProperties: TrackingProps,
  isAuth: boolean
): TrackingPropsInterface => {
  return {
    trackingContentPosition: trackingProperties?.trackingContentPosition,
    trackingDescription: trackingProperties?.trackingDescription,
    trackingOfferType: trackingProperties?.trackingOfferType,
    trackingTag: trackingProperties?.trackingTag1 ?? trackingProperties?.trackingTag,
    additionalTrackingVariables: 'false',
    atCCeVar48: trackingProperties?.atCCeVar48 ?? '',
    clickTrack: trackingProperties?.clickTrack ?? true,
    impressionTrack: trackingProperties?.impressionTrack ?? true,
    impressionCount: trackingProperties?.impressionCount ?? true,
    isAuth: isAuth,
    merchandisingCategory: trackingProperties?.merchandisingCategory,
    isDCAEnabled: true,
    spid: '',
    rpcCode: '',
    cell: '',
    offerCode: '',
    type: '',
    clickTrackValSeparator: ';',
  };
};

//Creating rendtions for koddi response
export const riottMediaDynamicMediaTransformation = (riottMediaAssets: MangoAssests) => {
  const getMangoRenditions = () => {
    const mangoRenditions = [];
    if (riottMediaAssets?.primaryImage_desktop && riottMediaAssets?.primaryImage_desktop !== '') {
      mangoRenditions.push({
        renditionPath: riottMediaAssets?.primaryImage_desktop,
        smartCropType: null,
        damPath: '',
        dynamic: true,
        height: 314,
        mediaQuery: 'min-width',
        mediaValue: '1200px',
        width: 558,
      });
    }
    if (
      (riottMediaAssets?.primaryImage_tablet && riottMediaAssets?.primaryImage_tablet !== '') ||
      (riottMediaAssets?.primaryImage_desktop && riottMediaAssets?.primaryImage_desktop !== '')
    ) {
      mangoRenditions.push({
        renditionPath: riottMediaAssets?.primaryImage_tablet ?? riottMediaAssets?.primaryImage_desktop,
        smartCropType: null,
        damPath: '',
        dynamic: true,
        height: 314,
        mediaQuery: 'min-width',
        mediaValue: '768px',
        width: 558,
      });
    }
    mangoRenditions.push({
      renditionPath: riottMediaAssets?.primaryImage_mobile ?? riottMediaAssets?.primaryImage_desktop,
      smartCropType: null,
      damPath: '',
      dynamic: true,
      height: 314,
      mediaQuery: 'max-width',
      mediaValue: '767px',
      width: 558,
    });
    return mangoRenditions;
  };

  const renditions = riottMediaAssets // Check if riottMediaAssets is defined
    ? getMangoRenditions()
    : []; // If riottMediaAssets is not defined, return an empty array

  const aemDynamicJSON = {
    altText: riottMediaAssets?.altText,
    damPath: '',
    assetPath: riottMediaAssets?.primaryImage_mobile ?? riottMediaAssets?.primaryImage_desktop,
    renditions: renditions,
  };
  return aemDynamicJSON;
};

export const isExternalLink = (linkHref: string, openInaNewTab: boolean) => {
  try {
    if (canUseDOM) {
      const currentDomain = window?.location?.hostname;
      const urlDomain = new URL(linkHref)?.hostname;
      return currentDomain !== urlDomain;
    } else {
      return openInaNewTab === true; // check for evergreen content on server side
    }
  } catch {
    return false;
  }
};

export const returnDecodedString = (input: string): string => {
  return input?.replaceAll('&amp;', '&') ?? '';
};

export const getDirection = () => {
  if (typeof document !== 'undefined') {
    const directionValue = document?.documentElement.getAttribute('dir');
    let direction: 'ltr' | 'rtl' | undefined;

    if (directionValue === 'ltr' || directionValue === 'rtl') {
      direction = directionValue;
    } else {
      direction = 'ltr';
    }
    return direction;
  } else {
    return 'ltr';
  }
};

export const removeParentTagRTE = (textRTE: string): string => {
  return textRTE?.substring(textRTE?.indexOf('>') + 1, textRTE?.lastIndexOf('<')) || textRTE;
};

export enum BadgeVariation {
  'Inline' = 'inline',
  'Overlay' = 'overlay',
}

export enum CAROUSEL {
  ID = 'merchandisingCarousel',
  ITEM = 'carousel__item',
  EVENT = 'updateCarousel',
  LOADER = 'skeleton-loader-composite',
}

export enum CAROUSEL_VARIATION {
  POST_CARD_HERO = 'post-card-hero',
  TILE_SQUARE = 'tile-square',
  TILE_WIDE = 'tile-wide',
  MERCHANDISING_DCA_WRAPPER = 'merchandising-dca-wrapper',
  PARTNER_CARD = 'partner card',
  TILE_SKINNY = 'tile-skinny',
}

/**
 *
 * @param ref it refers to the component-Ref authored inside carousel
 * @param isComponentNeeded if its false means, this component has to be removed from carousel
 * @description this method has to be called from carousel item to remove the skeleton loader
 */
export const updateCarousel = (
  ref: React.RefObject<HTMLElement>,
  isComponentNeeded = true,
  isMathVariation = false
) => {
  if (ref?.current) {
    const carouselItemNode = ref.current.closest('li.carousel__item');
    const carousel = ref.current?.closest(`#${CAROUSEL.ID}`);
    if (carouselItemNode && carousel) {
      const updatedCount = Number(carousel.getAttribute('data-count')) + 1;
      const maxCount = Number(carousel.getAttribute('data-total') || 0);
      carousel.setAttribute('data-count', String(updatedCount));
      if (isComponentNeeded === false) {
        carouselItemNode.remove();
        const deletedCount = Number(carousel.getAttribute('data-deleted-count')) + 1;
        carousel.setAttribute('data-deleted-count', String(deletedCount));
      }
      if (updatedCount >= maxCount) {
        carousel?.querySelectorAll(`li.${CAROUSEL.ITEM}`)?.forEach(item => {
          item?.classList?.remove(CAROUSEL.LOADER);
        });
        carousel.setAttribute('data-carousel-updated', 'true');
        carousel?.dispatchEvent(new CustomEvent(CAROUSEL.EVENT, { detail: { isMathVariation: isMathVariation } }));
      }
    } else {
      console.info('Element not authored inside carousel');
    }
  } else {
    console.error('while updating carousel either you called at server side or ref is not yet rendered');
  }
};

// add image domains to urls
export function formatImageUrl(url: string) {
  if (!url?.includes('https://')) {
    return `https://cache.marriott.com/${url}`;
  }
  return url;
}

type RecentlyViewedImageVariant =
  | 'square'
  | 'rectangle'
  | 'standardHorizontal'
  | 'panoramicHorizontal'
  | 'classicVertical';

const RECENTLY_VIEWED_RENDITION_BY_VARIANT: Record<RecentlyViewedImageVariant, string> = {
  square: 'Square',
  rectangle: 'Wide-Hor',
  standardHorizontal: 'Standard-Hor',
  panoramicHorizontal: 'Pano-Hor',
  classicVertical: 'Classic-Ver',
};

const CACHE_IMAGE_DOMAIN = 'https://cache.marriott.com';

function isRecord(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

function hasRecentlyViewedMetaCue(metaData: Record<string, unknown> | undefined): boolean {
  if (!metaData) return false;

  if (metaData['isRecentlyViewed'] === true || metaData['isRecentlyViewed'] === 'true') {
    return true;
  }
  return false;
}
function toAbsoluteCacheUrl(pathOrUrl: string): string {
  const value = (pathOrUrl || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return value.startsWith('/') ? `${CACHE_IMAGE_DOMAIN}${value}` : value;
}

function toRecentlyViewedVariantUrl(pathOrUrl: string, variant: RecentlyViewedImageVariant): string {
  const absolute = toAbsoluteCacheUrl(pathOrUrl);
  if (!absolute) return '';

  const [base, query] = absolute.split('?');
  if (!base.includes('/is/image/')) {
    return absolute;
  }

  const lastSlash = base.lastIndexOf('/');
  const lastColon = base.lastIndexOf(':');
  const rendition = RECENTLY_VIEWED_RENDITION_BY_VARIANT[variant];
  const variantBase = lastColon > lastSlash ? `${base.slice(0, lastColon)}:${rendition}` : `${base}:${rendition}`;

  return query ? `${variantBase}?${query}` : variantBase;
}

function getRecentlyViewedImageFromLocalStorage(variant: RecentlyViewedImageVariant): string {
  if (typeof window === 'undefined') return '';
  const stored = window.localStorage.getItem('miRecentlyViewedProperties');
  if (!stored) return '';

  try {
    const rv = JSON.parse(stored)?.recentlyViewedProperties?.[0] ?? {};
    const imageSrc = typeof rv?.imageSrc === 'string' ? rv.imageSrc : '';
    const imageUrl = typeof rv?.imageUrl === 'string' ? rv.imageUrl : '';

    const candidate = imageSrc && imageSrc?.indexOf('.tif') === -1 ? imageSrc : imageUrl;
    if (!candidate) return '';
    return toRecentlyViewedVariantUrl(candidate, variant);
  } catch {
    return '';
  }
}

export function getRecentlyViewedImageFallbackForPayload(
  payload: Record<string, unknown>,
  variant: RecentlyViewedImageVariant
): string {
  const rawMeta = payload['metaData'] ?? payload['metadata'];
  const metaData = isRecord(rawMeta) ? rawMeta : undefined;
  if (!hasRecentlyViewedMetaCue(metaData)) return '';

  return getRecentlyViewedImageFromLocalStorage(variant);
}
